"use client";

import { useState } from "react";
import { Link } from "@/i18n/navigation";
import { ArrowRight } from "lucide-react";
import { DirectionalIcon } from "@/components/ui/directional-icon";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

// Signal-syntax colours map onto the Aurora semantic tokens so the code panel
// reads correctly in both light and dark app themes.
const TOK = {
  sym: "text-primary",
  buy: "text-profit",
  sell: "text-destructive",
  key: "text-secondary",
  num: "text-warning",
  punct: "text-muted-foreground",
  comment: "text-muted-foreground",
} as const;

type Tok = keyof typeof TOK;
type Line = readonly [Tok, string][];

// Format examples are language-neutral signal syntax, not translatable copy.
const SAMPLES: readonly Line[][] = [
  // TradingView
  [
    [["sym", "EURUSD"], ["punct", ","], ["buy", "buy"], ["punct", ","], ["key", "sl"], ["punct", "="], ["num", "1.0820"], ["punct", ","], ["key", "tp"], ["punct", "="], ["num", "1.0910"], ["punct", ","], ["key", "risk"], ["punct", "="], ["num", "1%"]],
    [["sym", "XAUUSD"], ["punct", ","], ["sell", "sell"], ["punct", ","], ["key", "sl"], ["punct", "="], ["num", "2412"], ["punct", ","], ["key", "tp"], ["punct", "="], ["num", "2385"]],
  ],
  // Telegram
  [
    [["sym", "BTCUSD"], ["punct", ","], ["buy", "buy"], ["punct", ","], ["key", "lots"], ["punct", "="], ["num", "0.10"], ["punct", ","], ["key", "tp1"], ["punct", "="], ["num", "68950"], ["punct", ","], ["key", "tp2"], ["punct", "="], ["num", "69800"]],
    [["num", "#close"], ["punct", " "], ["sym", "BTCUSD"], ["punct", " "], ["comment", "// manage an open trade"]],
  ],
  // REST API
  [
    [["comment", "POST /v1/signal"]],
    [["sym", "GBPJPY"], ["punct", ","], ["buy", "buy"], ["punct", ","], ["key", "sl"], ["punct", "="], ["num", "189.40"], ["punct", ","], ["key", "trail"], ["punct", "="], ["num", "150"]],
  ],
] as const;

const TABS = ["tradingview", "telegram", "api"] as const;

export function SignalFormat() {
  const t = useTranslations("marketing.signalFormat");
  const [tab, setTab] = useState(0);

  return (
    <section className="scroll-mt-20 px-6 pb-24">
      <div className="mx-auto max-w-3xl">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <p className="mb-4 text-[13px] font-bold uppercase tracking-[0.14em] text-primary">
            {t("kicker")}
          </p>
          <h2 className="font-heading text-balance text-3xl font-extrabold sm:text-4xl">
            {t("title")}
          </h2>
          <p className="mx-auto mt-3.5 max-w-2xl text-balance text-muted-foreground">
            {t("subtitle")}
          </p>
        </div>

        {/* Code is direction-bound: force LTR per .claude/rules/i18n.md */}
        <div
          dir="ltr"
 className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-black/20 backdrop-blur"
        >
          <div className="flex items-center gap-1 border-b border-border bg-muted/30 px-2.5">
            {TABS.map((key, i) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(i)}
 className={cn(
                  "-mb-px cursor-pointer border-b-2 px-3.5 py-3 font-mono text-[12.5px] font-medium transition-colors",
                  tab === i
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t(`tabs.${key}`)}
              </button>
            ))}
            <span className="ms-auto pe-1.5 font-mono text-[10.5px] tracking-widest text-muted-foreground">
              .txt
            </span>
          </div>

          <div className="min-h-[118px] px-5 py-5 font-mono text-sm leading-loose">
            {SAMPLES[tab].map((line, li) => (
              <div key={li} className="flex gap-4">
                <span className="w-3.5 select-none text-end text-muted-foreground/50">
                  {li + 1}
                </span>
                <code className="whitespace-pre-wrap break-all">
                  {line.map(([tok, text], ti) => (
                    <span key={ti} className={TOK[tok]}>
                      {text}
                    </span>
                  ))}
                </code>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-4.5 text-center text-sm text-muted-foreground">
          {t("footNote")}{" "}
          <Link
            href="/docs/reference/signal-format"
 className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
          >
            {t("footLink")}
            <DirectionalIcon icon={ArrowRight} aria-hidden="true" className="size-3.5" />
          </Link>
        </p>

        {/* Execution platforms roadmap */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <span className="text-[13.5px] font-semibold text-muted-foreground">
            {t("platformsLabel")}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/[0.08] px-3.5 py-2 text-sm font-semibold">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-profit" />
            MetaTrader 5
            <span className="font-mono text-[10.5px] tracking-wide text-profit">
              {t("live")}
            </span>
          </span>
          {(["ctrader", "mt4"] as const).map((p) => (
            <span
              key={p}
 className="inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold text-muted-foreground"
            >
              {p === "ctrader" ? "cTrader" : "MT4"}
              <span className="font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">
                {t("soon")}
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
